import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Avatar, 
  Grid, 
  TextField, 
  Button, 
  Divider,
  styled,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PageContainer = styled(Box)({
  padding: '40px',
  backgroundColor: '#f8f9fa',
  minHeight: 'calc(100vh - 64px)'
});

const ProfileCard = styled(Paper)({
  padding: '32px',
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
});

const Title = styled(Typography)({
  fontSize: '1.75rem',
  fontWeight: 600,
  color: '#424242',
  marginBottom: '24px'
});

const ProfileAvatar = styled(Avatar)({
  width: '120px',
  height: '120px',
  fontSize: '3rem',
  margin: '0 auto 24px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
});

const BackButton = styled(Button)({
  marginTop: '24px'
});

function Profile() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const email = localStorage.getItem('user_email');
    if (!email) {
      navigate('/login');
      return;
    }
    
    setUserEmail(email);
    
    // Extract first and last name from email (before the @ symbol)
    if (email) {
      const namePart = email.split('@')[0];
      // Convert to title case and replace dots/underscores with spaces
      const fullName = namePart
        .replace(/[._]/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
      
      // Split into first and last name (if there's a space)
      const nameParts = fullName.split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
      
      setLoading(false);
    }
  }, [navigate]);
  
  const userInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <PageContainer>
      <Title>User Profile</Title>
      
      <ProfileCard>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ProfileAvatar sx={{ bgcolor: '#1976d2' }}>
            {userInitial}
          </ProfileAvatar>
          <Typography variant="h5" gutterBottom>
            {firstName} {lastName}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {userEmail}
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              disabled
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              disabled
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              value={userEmail}
              disabled
              variant="outlined"
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary" paragraph>
            This information is extracted from your Google account.
          </Typography>
        </Box>
        
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/home')}
          variant="outlined"
        >
          Back to Dashboard
        </BackButton>
      </ProfileCard>
    </PageContainer>
  );
}

export default Profile;
